"use client";

import { useEffect, useMemo, useState } from "react";
import { Copy, Check, ExternalLink, Link2 } from "lucide-react";
import { Button } from "../button.js";
import { Input } from "../input.js";
import { Label } from "../label.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../select.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../tabs.js";
import { useToast } from "../../hooks/use-toast.js";
import { PERSON_OPTIONS, DURATION_OPTIONS } from "./utils/bookingConstants.js";

/**
 * Shape returned by GET /api/booking/event-types (public route). Mirrors
 * EventTypeConfig from lib/booking/event-types-config without importing the
 * server module into a client component.
 */
interface EventTypeOption {
  eventType: string;
  name: string;
  availableDurations: number[];
  defaultDuration?: number;
  description?: string;
}

type BuilderMode = "event-type" | "person";

const PUBLIC_BOOKING_PATH = "/crm/calendar/public-booking";

/**
 * Admin-only tool that composes shareable public-booking links. It only builds
 * URL query params from already-public data (event-type configs + the fixed
 * team list) — it performs no privileged reads and no writes. The link points
 * at the public booking flow; a meeting is only created when the recipient
 * completes that flow themselves.
 */
export function BookingLinkBuilderPanel() {
  const { toast } = useToast();

  const [mode, setMode] = useState<BuilderMode>("event-type");

  // Event-type mode state
  const [eventTypes, setEventTypes] = useState<EventTypeOption[]>([]);
  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState("");
  const [prefillName, setPrefillName] = useState("");
  const [prefillEmail, setPrefillEmail] = useState("");

  // Person mode state
  const [selectedPerson, setSelectedPerson] = useState("");
  const [personDuration, setPersonDuration] = useState<number>(30);

  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Load the public event-type catalog once.
  useEffect(() => {
    let cancelled = false;
    setIsLoadingEventTypes(true);
    fetch("/api/booking/event-types")
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setEventTypes(Array.isArray(data?.eventTypes) ? data.eventTypes : []);
      })
      .catch(() => {
        if (!cancelled) setEventTypes([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEventTypes(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedEventTypeConfig = useMemo(
    () => eventTypes.find((et) => et.eventType === selectedEventType) ?? null,
    [eventTypes, selectedEventType],
  );

  // Reset the generated URL whenever inputs change so a stale link is never
  // copied after the selection moved on.
  useEffect(() => {
    setGeneratedUrl("");
    setCopied(false);
  }, [mode, selectedEventType, prefillName, prefillEmail, selectedPerson, personDuration]);

  const buildUrl = (): string | null => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams();

    if (mode === "event-type") {
      if (!selectedEventType) {
        toast({
          title: "Event type required",
          description: "Pick an event type to build its booking link.",
          variant: "destructive",
        });
        return null;
      }
      params.set("eventType", selectedEventType);
      // In event-type mode these prefill the BOOKER's identity (see
      // PublicBookingStepper URL handling), not the bookee.
      if (prefillName.trim()) params.set("name", prefillName.trim());
      if (prefillEmail.trim()) params.set("email", prefillEmail.trim());
    } else {
      if (!selectedPerson) {
        toast({
          title: "Team member required",
          description: "Pick whose calendar this link should book.",
          variant: "destructive",
        });
        return null;
      }
      // In person mode `email` is the bookee (the team member's calendar).
      params.set("email", selectedPerson);
      params.set("duration", String(personDuration));
    }

    return `${window.location.origin}${PUBLIC_BOOKING_PATH}?${params.toString()}`;
  };

  const handleGenerate = () => {
    const url = buildUrl();
    if (url) setGeneratedUrl(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      toast({ title: "Link copied", description: "Booking link copied to your clipboard." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Copy failed",
        description: "Could not access the clipboard. Copy the link manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={mode} onValueChange={(v) => setMode(v as BuilderMode)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="event-type">By event type</TabsTrigger>
          <TabsTrigger value="person">By specific person</TabsTrigger>
        </TabsList>

        <TabsContent value="event-type" className="space-y-4 pt-2">
          <div>
            <Label htmlFor="eventType">Event type *</Label>
            <Select value={selectedEventType} onValueChange={setSelectedEventType} disabled={isLoadingEventTypes}>
              <SelectTrigger id="eventType" className="mt-1">
                <SelectValue placeholder={isLoadingEventTypes ? "Loading…" : "Select an event type"} />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((et) => (
                  <SelectItem key={et.eventType} value={et.eventType}>
                    {et.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedEventTypeConfig?.description && (
              <p className="mt-1 text-xs text-muted-foreground">{selectedEventTypeConfig.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="prefillName">Prefill name (optional)</Label>
              <Input
                id="prefillName"
                value={prefillName}
                onChange={(e) => setPrefillName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="prefillEmail">Prefill email (optional)</Label>
              <Input
                id="prefillEmail"
                type="email"
                value={prefillEmail}
                onChange={(e) => setPrefillEmail(e.target.value)}
                placeholder="jane@example.com"
                className="mt-1"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Event-type links assign a team member automatically (round robin). Prefill fields fill in the recipient's own
            name and email on the form.
          </p>
        </TabsContent>

        <TabsContent value="person" className="space-y-4 pt-2">
          <div>
            <Label htmlFor="person">Team member *</Label>
            <Select value={selectedPerson} onValueChange={setSelectedPerson}>
              <SelectTrigger id="person" className="mt-1">
                <SelectValue placeholder="Select whose calendar to book" />
              </SelectTrigger>
              <SelectContent>
                {PERSON_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="personDuration">Default meeting duration</Label>
            <Select value={String(personDuration)} onValueChange={(v) => setPersonDuration(parseInt(v, 10))}>
              <SelectTrigger id="personDuration" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((d) => (
                  <SelectItem key={d.value} value={String(d.value)}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Direct-person links book the selected team member's calendar specifically — no round robin.
          </p>
        </TabsContent>
      </Tabs>

      <Button type="button" onClick={handleGenerate} className="w-full">
        <Link2 className="mr-2 h-4 w-4" />
        Generate booking link
      </Button>

      {generatedUrl && (
        <div className="space-y-3">
          <div>
            <Label>Shareable booking link</Label>
            <div className="mt-1 flex gap-2">
              <Input value={generatedUrl} readOnly className="flex-1 text-sm" />
              <Button type="button" size="sm" variant="outline" onClick={handleCopy} className="px-3">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => window.open(generatedUrl, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Test link
          </Button>
        </div>
      )}
    </div>
  );
}
